import type { components } from "@certn/api/generated-schemas/client-portal";
import type { RegionEntitlementDeniedBody } from "@certn/api";
import { createContext, useContext } from "react";

import {
  CLIENT_PORTAL_PUBLIC_CACHE_ALLOWLIST,
  isProtectedClientPortalApiRequest,
  isProtectedClientPortalCacheKey,
  isPublicClientPortalCacheKey,
  normalizeClientPortalCacheKeyPath,
} from "./clientPortalCacheAllowlist";

export const CLIENT_PORTAL_ME_PATH = "/api/internal/client-portal/me/" as const;

export const ACTIVATION_COALESCE_MS = 50;

export type ClientPortalBootstrapStatus =
  | "pending"
  | "authorized"
  | "denied"
  | "error";

export type ClientPortalUserDetails =
  components["schemas"]["LoggedInUserDetail"];

export type { RegionEntitlementDeniedBody };

type ClientPortalBootstrapContextValue = {
  status: ClientPortalBootstrapStatus;
  userDetails: ClientPortalUserDetails | undefined;
  denialBody: RegionEntitlementDeniedBody | undefined;
  isAuthorizationPending: boolean;
  authorizationError: unknown;
  mutateUser: () => Promise<ClientPortalUserDetails | undefined>;
  purgeProtectedCache: () => Promise<void>;
  recoverFromDenialAfterSignup: () => Promise<void>;
};

export const ClientPortalBootstrapContext =
  createContext<ClientPortalBootstrapContextValue | null>(null);

export function useClientPortalBootstrap(): ClientPortalBootstrapContextValue {
  const context = useContext(ClientPortalBootstrapContext);
  if (!context) {
    throw new Error(
      "useClientPortalBootstrap must be used within ClientPortalBootstrapProvider"
    );
  }
  return context;
}

export function useOptionalClientPortalBootstrap(): ClientPortalBootstrapContextValue | null {
  return useContext(ClientPortalBootstrapContext);
}

export {
  CLIENT_PORTAL_PUBLIC_CACHE_ALLOWLIST,
  isProtectedClientPortalApiRequest,
  isProtectedClientPortalCacheKey,
  isPublicClientPortalCacheKey,
  normalizeClientPortalCacheKeyPath,
};
